// src/pages/FundPlanner/lib/solverMain.js
import dayjs from "lib/dayjs";
import { dayCountFraction, daysBetween } from "lib/dayCount";
import { solveGrossFundingStructure } from "./solverGross";
import { solveNetFundingStructure } from "./solverNet";

/* ──────────────────────────────────────────────────────────
   Shared Utility Functions for FundPlanner Solvers
────────────────────────────────────────────────────────── */

export const SLGS_DENOM_UNIT = 1;
export const SLGS_ROUND_MODE = "nearest";
export const SLGS_COUPON_MIN_DAYS_AFTER_SETTLEMENT = 30;

export const DEBUG_SOLVER = false;
export const dlog = () => {};
export const dgroup = () => {};
export const dgroupCollapsed = () => {};
export const dend = () => {};

export function roundToDenomination(value, unit = 1, mode = "nearest") {
  if (!unit || unit <= 0) return value;
  const q = value / unit;
  switch (mode) {
    case "floor":
      return Math.floor(q) * unit;
    case "ceil":
      return Math.ceil(q) * unit;
    default:
      return Math.round(q) * unit;
  }
}

/**
 * SLGS certificate/note maturity rule.
 *
 * Certificates are used for any cashflow with a maturity date on or before
 * exactly one calendar year from the settlement date.
 *
 * Notes are used only for maturities after that inclusive one-year boundary,
 * meaning the earliest note maturity is settlement date + 1 year + 1 day.
 */
export function getSlgsCertificateCutoffDate(settlementDate) {
  if (!settlementDate) return null;
  const settle = dayjs(settlementDate);
  if (!settle.isValid()) return null;
  return settle.startOf("day").add(1, "year");
}

export function isSlgsCertificateMaturity(maturityDate, settlementDate) {
  if (!maturityDate || !settlementDate) return false;

  const maturity = dayjs(maturityDate);
  const cutoff = getSlgsCertificateCutoffDate(settlementDate);

  if (!maturity.isValid() || !cutoff?.isValid?.()) return false;

  return maturity.startOf("day").isSameOrBefore(cutoff, "day");
}

export function isSlgsNoteMaturity(maturityDate, settlementDate) {
  if (!maturityDate || !settlementDate) return false;

  const maturity = dayjs(maturityDate);
  const cutoff = getSlgsCertificateCutoffDate(settlementDate);

  if (!maturity.isValid() || !cutoff?.isValid?.()) return false;

  return maturity.startOf("day").isAfter(cutoff, "day");
}

// Still needed for SLGS Notes (coupon dates still anchored to maturity)
export function buildSLGSCouponDatesAnchoredToMaturity(maturity, settlement) {
  const end = dayjs(maturity);
  const start = dayjs(settlement);
  const maturityDay = Math.min(end.date(), 30);
  const out = [];
  let cd = end.clone();
  while (cd.isAfter(start, "day")) {
    out.unshift(cd.clone());
    cd = cd.subtract(6, "month").date(maturityDay);
  }
  return out;
}

export function tenorToMonths(tenor) {
  const m = tenor?.match(/(?:(\d+)y)?(?:(\d+)m)?/i);
  if (!m) return null;
  const years = parseInt(m[1] || "0", 10);
  const mons = parseInt(m[2] || "0", 10);
  return years * 12 + mons;
}

export function deriveMaturity(baseDate, tenor) {
  if (!tenor || typeof tenor !== "string") return null;
  const m = tenor.match(/^(\d+)([mMyY])$/);
  if (m) {
    const v = parseInt(m[1], 10);
    const unit = m[2].toLowerCase() === "m" ? "month" : "year";
    return dayjs(baseDate).add(v, unit);
  }
  const ym = tenor.match(/^(\d+)y(\d+)m$/i);
  if (ym)
    return dayjs(baseDate)
      .add(parseInt(ym[1], 10), "year")
      .add(parseInt(ym[2], 10), "month");
  return null;
}

/* Removed subtractBusinessDays — no more leadDays shifting */

/* SLGS Bands: these stay unchanged */
export function buildSlgsBands(settlementDate, slgsRowsSorted) {
  const base = dayjs(settlementDate).add(30, "day");
  return slgsRowsSorted.map((row, i) => {
    const start = base.add(i, "month");
    const end = base.add(i + 1, "month");
    return {
      ...row,
      bandStart: start,
      bandEnd: end,
      bandStartStr: start.format("YYYY-MM-DD"),
      bandEndStr: end.format("YYYY-MM-DD"),
    };
  });
}

export function findSlgsBandForMaturity(maturityDate, bands) {
  const m = dayjs(maturityDate);
  for (const b of bands) {
    if (m.isSameOrAfter(b.bandStart, "day") && m.isBefore(b.bandEnd, "day")) {
      return b;
    }
  }
  if (!bands.length) return null;
  if (m.isBefore(bands[0].bandStart, "day")) return bands[0];
  return bands[bands.length - 1];
}

export function dcfLogged(from, to, basis) {
  const startStr = dayjs(from).format("YYYY-MM-DD");
  const endStr = dayjs(to).format("YYYY-MM-DD");
  const diffDays = daysBetween(startStr, endStr);
  const val = dayCountFraction(startStr, endStr, basis);
  dlog("DCF", { basis, startStr, endStr, diffDays, result: val });
  return val;
}

/* ──────────────────────────────────────────────────────────
   Unified Solver Entry Point
────────────────────────────────────────────────────────── */

export async function solveFundingStructure(opts) {
  const {
    mode = "gross",
    draws = [], // now: [{ id, date, amount }]
    rates = [],
    securityType = "slgs",
    asOfDate,
    subscriptionDate,
    settlementDate,
    dayCount = "act/act",
  } = opts;

  const today = dayjs();
  const asOf = asOfDate || rates[0]?.asOf || today.format("YYYY-MM-DD");
  const subDate = subscriptionDate || asOf;
  const settleDate = settlementDate || subDate;

  let result;
  try {
    if (mode === "gross") {
      result = await solveGrossFundingStructure(
        draws,
        rates,
        dayCount,
        securityType,
        subDate,
        settleDate
      );
    } else if (mode === "net") {
      result = await solveNetFundingStructure(
        draws,
        rates,
        dayCount,
        securityType,
        subDate,
        settleDate
      );
    } else {
      throw new Error(`Unknown mode: ${mode}`);
    }
  } catch (err) {
    console.error(`Solver (${mode}) failed:`, err);
    throw err;
  }

  const investments = Array.isArray(result)
    ? result
    : Array.isArray(result?.investments)
    ? result.investments
    : [];

  const inflows = result?.__inflows || result?.inflows || [];

  const totalPrincipal = investments.reduce((s, r) => s + (r.principal || 0), 0);
  const totalCost = investments.reduce((s, r) => s + (r.cost || 0), 0);
  const avgRate =
    investments.length > 0
      ? investments.reduce((s, r) => s + (r.ratePct || 0), 0) /
        investments.length
      : null;

  const summary = {
    mode,
    securityType,
    asOf,
    subscriptionDate: subDate,
    settlementDate: settleDate,
    totalPrincipal,
    totalCost,
    avgRate,
    count: investments.length,
  };

  return { investments, inflows, summary };
}
